import { NavSection, NavItem } from '@/types/navigation';

/**
 * Permission-based navigation configuration
 * Navigation visibility based on user permissions only
 * No role-based logic
 */

export const NAVIGATION_SECTIONS: NavSection[] = [
  // =====================================================
  // SYSTEM ADMIN SECTIONS (Super Admin only)
  // =====================================================
  {
    id: 'system-overview',
    title: 'SYSTEM OVERVIEW',
    domain: 'admin',
    requiredPermissions: ['admin.dashboard.view'],
    items: [
      {
        id: 'admin-dashboard',
        title: 'Admin Dashboard',
        path: '/admin/dashboard',
        icon: 'admin-dashboard',
        permission: 'admin.dashboard.view'
      },
    ]
  },
  {
    id: 'platform-management',
    title: 'PLATFORM MANAGEMENT',
    domain: 'admin',
    requiredPermissions: ['companies.read', 'users.read', 'system.config'],
    items: [
      {
        id: 'companies',
        title: 'Companies',
        path: '/admin/companies',
        icon: 'companies',
        permission: 'companies.read'
      },
      {
        id: 'audit-periods-inbox',
        title: 'External Audits',
        path: '/admin/audit-periods',
        icon: 'security',
        permission: 'admin.dashboard.view',
      },
      {
        id: 'admin-messages',
        title: 'Messages',
        path: '/admin/messages',
        icon: 'messages',
        permission: 'compliance.read'
      },
      {
        id: 'users',
        title: 'Users',
        path: '/admin/users',
        icon: 'users',
        permission: 'users.read'
      },
      {
        id: 'user-types',
        title: 'Permission Profiles',
        path: '/admin/permission-profiles',
        icon: 'roles',
        permission: 'users.read'
      },
      {
        id: 'access-control',
        title: 'Expiry & Access Control',
        path: '/admin/access-control',
        icon: 'expiry-access',
        permission: 'system.config'
      }
    ]
  },
  {
    id: 'standards-frameworks',
    title: 'STANDARDS & FRAMEWORKS',
    domain: 'admin',
    requiredPermissions: ['frameworks.read', 'templates.create', 'templates.update'],
    items: [
      // Framework Library (canned-catalog-style deploy) hidden from nav:
      // every framework on the platform is now produced by parsing a real
      // source document through the Architect pipeline, so the curated
      // catalog path is obsolete. Route + backend intentionally retained in
      // case the model changes; restore by uncommenting this entry.
      // {
      //   id: 'framework-library',
      //   title: 'Framework Library',
      //   path: '/compliance/definition/library',
      //   icon: 'library',
      //   permission: 'frameworks.read'
      // },
      {
        id: 'document-library',
        title: 'Document Library',
        path: '/admin/document-library',
        icon: 'document-library',
        permission: 'frameworks.read'
      },
      {
        id: 'frameworks',
        title: 'Frameworks',
        path: '/compliance/definition/frameworks',
        icon: 'guidance',
        permission: 'frameworks.read'
      },
      {
        id: 'compliance-structure',
        title: 'Compliance Structure Builder',
        path: '/compliance/definition/builder',
        icon: 'compliance-structure',
        permission: 'templates.read'
      },
      {
        id: 'standard-upload',
        title: 'Document Parser',
        path: '/compliance/definition/parser',
        icon: 'standard-upload',
        permission: 'templates.create'
      },
      {
        id: 'templates',
        title: 'Templates',
        path: '/compliance/definition/templates',
        icon: 'active-frameworks',
        permission: 'templates.read'
      },
      // Phase 3: /compliance/definition/mapping demoted from primary nav.
      // The builder now hosts mapping inline. Route is preserved as a power-user
      // bulk-ops surface only. See docs/COMPLIANCE_LIFECYCLE_PLAN.md §7 Phase 3.
    ]
  },
  {
    id: 'prepopulation-templates',
    title: 'PREPOPULATION & TEMPLATES',
    domain: 'admin',
    requiredPermissions: ['frameworks.assign', 'templates.update', 'system.config'],
    items: [
      // Phase 3: /compliance/definition/assignments demoted from primary nav.
      // The builder's "Assign to Company" inline modal replaces the standard
      // happy path. Route preserved for bulk operations only.
      {
        id: 'guidance',
        title: 'Guidance & Process Maps',
        path: '/compliance/definition/guidance',
        icon: 'guidance',
        permission: 'templates.update'
      },
      {
        id: 'email-center',
        title: 'Email Center',
        path: '/admin/email-center',
        icon: 'email-templates',
        permission: 'system.config'
      }
    ]
  },
  {
    id: 'audit-governance',
    title: 'AUDIT & GOVERNANCE',
    domain: 'admin',
    requiredPermissions: ['audit_logs.view', 'system_logs.view', 'system.config'],
    items: [
      {
        id: 'audit-logs',
        title: 'Audit Logs',
        path: '/admin/audit-logs',
        icon: 'audit-logs',
        permission: 'audit_logs.view'
      },
      {
        id: 'system-logs',
        title: 'System Logs',
        path: '/admin/system-logs',
        icon: 'system-logs',
        permission: 'system_logs.view'
      },
      {
        id: 'troubleshoot-agent',
        title: 'Troubleshoot Agent',
        path: '/admin/troubleshoot-agent',
        icon: 'bot',
        permission: 'system.config',
        allowedEmails: ['support@yiontech.co.uk']
      }
    ]
  },
  {
    id: 'account-governance',
    title: 'ACCOUNT GOVERNANCE',
    domain: 'admin',
    requiredPermissions: ['system.config'],
    items: [
      {
        id: 'account-settings',
        title: 'Account Settings',
        path: '/settings',
        icon: 'settings',
        permission: 'system.config'
      },
      {
        id: 'system-settings',
        title: 'System Settings',
        path: '/admin/system-settings',
        icon: 'system-settings',
        permission: 'system.config'
      },
      {
        id: 'ai-capability-manager',
        title: 'AI Capability Manager',
        path: '/admin/ai/capabilities',
        icon: 'bot',
        permission: 'system.config'
      },
      {
        id: 'system-health',
        title: 'System Health',
        path: '/admin/system-health',
        icon: 'dashboard',
        permission: 'system.config'
      },
      {
        id: 'pages',
        title: 'Pages',
        path: '/admin/pages',
        icon: 'pages',
        permission: 'system.config'
      },
      {
        id: 'gdpr',
        title: 'GDPR Compliance',
        path: '/admin/gdpr',
        icon: 'gdpr',
        permission: 'system.config'
      },
      {
        id: 'billing-admin',
        title: 'Billing & Subscriptions',
        path: '/admin/billing',
        icon: 'billing',
        permission: 'system.config'
      },
      {
        id: 'settlement-dashboard',
        title: 'Platform Settlement',
        path: '/admin/settlement',
        icon: 'billing',
        permission: 'system.config'
      },
      {
        id: 'ai-usage',
        title: 'AI Usage & Cost',
        path: '/admin/ai-usage',
        icon: 'system-monitor',
        permission: 'system.monitor'
      },
      {
        id: 'partners',
        title: 'Partner Organisations',
        path: '/admin/partners',
        icon: 'roles',
        permission: 'system.config'
      }
    ]
  },
  {
    id: 'admin-integrations',
    title: 'INTEGRATIONS & SECURITY',
    domain: 'admin',
    requiredPermissions: ['system.config'],
    items: [
      {
        id: 'admin-iot',
        title: 'IoT Devices',
        path: '/admin/iot',
        icon: 'dashboard',
        permission: 'system.config'
      },
      {
        id: 'admin-connectors',
        title: 'Connectors',
        path: '/admin/connectors',
        icon: 'framework-mapping',
        permission: 'system.config'
      },
      {
        id: 'admin-sso',
        title: 'SSO Configuration',
        path: '/admin/sso',
        icon: 'roles',
        permission: 'system.config'
      },
      {
        id: 'admin-api-keys',
        title: 'API Keys',
        path: '/admin/api-keys',
        icon: 'settings',
        permission: 'system.config'
      },
      {
        id: 'admin-blockchain-audit',
        title: 'Blockchain Audit Trail',
        path: '/admin/blockchain-audit',
        icon: 'audit-logs',
        permission: 'system.config'
      },
      {
        id: 'admin-security-scanning',
        title: 'Security Scanning',
        path: '/admin/security-scanning',
        icon: 'system-settings',
        permission: 'system.config'
      }
    ]
  },

  // =====================================================
  // COMPANY ADMIN SECTIONS (Company Admin only)
  // =====================================================
  // PARTNER ADMIN SECTIONS
  // =====================================================
  {
    id: 'partner-overview',
    title: 'PORTFOLIO',
    domain: 'partner' as any,
    requiredPermissions: ['partner.dashboard.view'],
    items: [
      {
        id: 'partner-dashboard',
        title: 'Dashboard',
        path: '/partner/dashboard',
        icon: 'admin-dashboard',
        permission: 'partner.dashboard.view'
      },
      {
        id: 'partner-companies',
        title: 'My Companies',
        path: '/partner/companies',
        icon: 'companies',
        permission: 'partners.manage'
      },
      {
        id: 'partner-users',
        title: 'Users',
        path: '/partner/users',
        icon: 'users',
        permission: 'partners.manage'
      }
    ]
  },
  {
    id: 'partner-frameworks',
    title: 'STANDARDS & FRAMEWORKS',
    domain: 'partner' as any,
    requiredPermissions: ['frameworks.read'],
    items: [
      // Framework Library hidden from partner nav — see note on the admin
      // sidebar entry above. Partners receive frameworks exclusively via
      // framework_assignments granted by a system admin.
      // {
      //   id: 'partner-framework-library',
      //   title: 'Framework Library',
      //   path: '/compliance/definition/library',
      //   icon: 'compliance-structure',
      //   permission: 'frameworks.read'
      // },
      {
        id: 'partner-frameworks-list',
        title: 'Frameworks',
        path: '/compliance/definition/frameworks',
        icon: 'guidance',
        permission: 'frameworks.read'
      },
      {
        id: 'partner-framework-builder',
        title: 'Compliance Structure Builder',
        path: '/compliance/definition/builder',
        icon: 'compliance-structure',
        permission: 'templates.read'
      },
      // Parser is system-admin-only (any user whose profile has no partner_id
      // and no company_id). Partners consume frameworks granted via
      // framework_assignments, never via the parser directly.
      // Phase 3: partner mapping + assignments demoted from primary nav.
      // Builder is now the orchestration hub. See COMPLIANCE_LIFECYCLE_PLAN.md §7 Phase 3.
      {
        id: 'partner-templates',
        title: 'Templates',
        path: '/compliance/definition/templates',
        icon: 'active-frameworks',
        permission: 'templates.read'
      },
      {
        id: 'partner-guidance',
        title: 'Guidance & Process Maps',
        path: '/compliance/definition/guidance',
        icon: 'guidance',
        permission: 'templates.update'
      }
    ]
  },
  {
    id: 'partner-tools',
    title: 'TOOLS',
    domain: 'partner' as any,
    requiredPermissions: ['partners.manage'],
    items: [
      {
        id: 'partner-audit-logs',
        title: 'Audit Logs',
        path: '/partner/audit-logs',
        icon: 'audit-logs',
        permission: 'partners.manage'
      },
      {
        id: 'partner-messages',
        title: 'Messages',
        path: '/partner/messages',
        icon: 'messages',
        permission: 'notifications.read'
      },
      {
        id: 'partner-billing',
        title: 'Billing & Invoices',
        path: '/partner/billing',
        icon: 'billing',
        permission: 'partners.manage'
      },
      {
        id: 'partner-account-settings',
        title: 'Account Settings',
        path: '/settings',
        icon: 'settings',
        permission: 'partners.manage'
      }
    ]
  },
  // =====================================================
  {
    id: 'company-overview',
    title: 'OVERVIEW',
    domain: 'company',
    requiredPermissions: ['company.dashboard.view'],
    items: [
      {
        id: 'company-dashboard',
        title: 'Company Dashboard',
        path: '/company/dashboard',
        icon: 'company-dashboard',
        permission: 'company.dashboard.view'
      }
    ]
  },
  {
    id: 'company-compliance-journeys',
    title: 'COMPLIANCE JOURNEYS',
    domain: 'company',
    requiredPermissions: ['company.compliance.manage', 'companies.read'],
    items: [
      {
        id: 'compliance-pages',
        title: 'Compliance Page Management',
        path: '/company/compliance-pages',
        icon: 'framework-manager',
        permission: 'company.compliance.manage'
      },
      {
        id: 'company-frameworks',
        title: 'Frameworks / Standards',
        path: '/company/frameworks',
        icon: 'framework-manager',
        permission: 'companies.read'
      }
    ]
  },
  {
    id: 'company-evidence-documents',
    title: 'EVIDENCE & DOCUMENTS',
    domain: 'company',
    requiredPermissions: ['compliance.read'],
    items: [
      {
        id: 'evidence-library',
        title: 'Evidence Library',
        path: '/company/evidence',
        icon: 'evidence-library',
        permission: 'compliance.read'
      }
      // "Uploaded Documents" hidden — overlaps with Evidence Library and Policy
      // Library. Will consolidate into a unified document manager in a follow-up.
    ]
  },
  {
    id: 'company-users-access',
    title: 'USERS & ACCESS',
    domain: 'company',
    requiredPermissions: ['users.read'],
    items: [
      {
        id: 'company-users',
        title: 'Company Users',
        path: '/company/users',
        icon: 'users',
        permission: 'users.read'
      },
      {
        id: 'company-user-types',
        title: 'Permission Profiles',
        path: '/company/permission-profiles',
        icon: 'roles',
        permission: 'users.read'
      },

    ]
  },
  // Company Admin - Compliance Management
  {
    id: 'company-compliance-management',
    title: 'COMPLIANCE MANAGEMENT',
    domain: 'company',
    requiredPermissions: ['compliance.read'],
    items: [
      { id: 'company-obligation-register', title: 'Obligation Register', path: '/compliance/obligations', icon: 'obligation-register', permission: 'compliance.read' },
      { id: 'company-task-management', title: 'Task Management', path: '/compliance/task-management', icon: 'assigned-pages', permission: 'compliance.read' },
      { id: 'company-capa', title: 'Corrective Actions (CAPA)', path: '/compliance/capa', icon: 'assigned-pages', permission: 'compliance.read' },
      { id: 'company-evidence-expiry', title: 'Evidence Expiry', path: '/compliance/evidence-expiry', icon: 'pending-evidence', permission: 'compliance.read' },
      { id: 'company-control-tests', title: 'Control Testing', path: '/compliance/control-tests', icon: 'control-tests', permission: 'compliance.read' },
      { id: 'company-training', title: 'Training & Awareness', path: '/compliance/training', icon: 'assigned-pages', permission: 'compliance.read' }
    ]
  },
  // Company Admin - GDPR & Data Protection
  {
    id: 'company-data-protection',
    title: 'GDPR & DATA PROTECTION',
    domain: 'company',
    requiredPermissions: ['compliance.read'],
    items: [
      { id: 'company-ropa', title: 'ROPA Register', path: '/compliance/ropa', icon: 'reference-materials', permission: 'compliance.read' }
    ]
  },
  // Company Admin - Governance & Risk
  {
    id: 'company-governance',
    title: 'GOVERNANCE & RISK',
    domain: 'company',
    requiredPermissions: ['compliance.read'],
    items: [
      { id: 'company-policy-library', title: 'Policy Library', path: '/compliance/policies', icon: 'reference-materials', permission: 'compliance.read' },
      { id: 'company-policy-attestations', title: 'Policy Attestations', path: '/compliance/policy-attestations', icon: 'policy-attestations', permission: 'compliance.read' },
      { id: 'company-risk-register', title: 'Risk Register', path: '/compliance/risk-register', icon: 'compliance-structure', permission: 'compliance.read' },
      { id: 'company-controls-library', title: 'Controls Library', path: '/compliance/controls', icon: 'compliance-structure', permission: 'compliance.read' },
      { id: 'company-vendor-risk', title: 'Vendor Risk Register', path: '/compliance/vendor-risk', icon: 'compliance-structure', permission: 'compliance.read' },
      { id: 'company-vendor-contracts', title: 'Vendor Contracts & DPA', path: '/compliance/vendor-contracts', icon: 'vendor-contracts', permission: 'compliance.read' },
      { id: 'company-asset-inventory', title: 'Asset Inventory', path: '/compliance/assets', icon: 'compliance-structure', permission: 'compliance.read' }
    ]
  },
  // Company Admin - Incident & Breach Management
  {
    id: 'company-incident-management',
    title: 'INCIDENT MANAGEMENT',
    domain: 'company',
    requiredPermissions: ['compliance.read'],
    items: [
      { id: 'company-incidents', title: 'Incident Register', path: '/compliance/incidents', icon: 'incident-register', permission: 'compliance.read' },
      { id: 'company-privacy-reviews', title: 'Privacy Reviews', path: '/compliance/privacy-reviews', icon: 'privacy-reviews', permission: 'compliance.read' },
      { id: 'company-data-retention', title: 'Data Retention', path: '/compliance/data-retention', icon: 'data-retention', permission: 'compliance.read' }
    ]
  },
  // Company Admin - Audit & Assurance (ISO 19011)
  {
    id: 'company-audit-assurance',
    title: 'AUDIT & ASSURANCE',
    domain: 'company',
    requiredPermissions: ['compliance.read'],
    items: [
      { id: 'company-certifications', title: 'Certifications', path: '/company/certifications', icon: 'compliance-structure', permission: 'companies.read' },
      { id: 'company-management-reviews', title: 'Management Reviews', path: '/compliance/management-reviews', icon: 'reference-materials', permission: 'compliance.read' },
      { id: 'company-audit-logs', title: 'Audit Logs', path: '/compliance/company-audit-logs', icon: 'audit-logs', permission: 'compliance.read' }
    ]
  },
  // UK REGULATORY — hidden from sidebar until these are refactored into the
  // Frameworks system as selectable standards (like ISO 27001). The routes and
  // pages still exist; they're just not in the nav. Keeps the sidebar focused
  // on the universal compliance workflow and prevents confusion during demos.
  // Restore by uncommenting when we build the UK-regulatory-specific framework
  // templates. See docs/COMPLIANCE_LIFECYCLE_PLAN.md.
  // Company Admin - Reporting
  {
    id: 'company-reporting',
    title: 'REPORTING',
    domain: 'company',
    requiredPermissions: ['compliance.read'],
    items: [
      { id: 'company-monitoring', title: 'Monitoring Dashboard', path: '/compliance/monitoring', icon: 'compliance-dashboard', permission: 'compliance.read' },
      { id: 'company-management-reports', title: 'Management Reports', path: '/compliance/management-reports', icon: 'reference-materials', permission: 'compliance.read' },
      { id: 'company-compliance-timeline', title: 'Compliance Timeline', path: '/compliance/timeline', icon: 'compliance-dashboard', permission: 'compliance.read' },
      { id: 'company-trust-portal', title: 'Trust Portal', path: '/compliance/trust-portal', icon: 'reference-materials', permission: 'compliance.read' }
    ]
  },
  {
    id: 'company-carbon-management',
    title: 'CARBON & EMISSIONS',
    domain: 'company',
    requiredPermissions: ['compliance.read'],
    items: [
      { id: 'company-emissions-scopes', title: 'Scopes Dashboard', path: '/emissions/scopes', icon: 'emissions-dashboard', permission: 'compliance.read' },
      { id: 'company-emissions-bulk', title: 'Bulk Calculator', path: '/emissions/bulk-calculator', icon: 'bulk-calculator', permission: 'compliance.read' },
      { id: 'company-emissions-import', title: 'Excel Import', path: '/emissions/excel-import', icon: 'bulk-calculator', permission: 'compliance.read' },
      { id: 'company-emissions-targets', title: 'Reduction Targets', path: '/emissions/targets', icon: 'reduction-targets', permission: 'compliance.read' },
      { id: 'company-emissions-offsets', title: 'Offset Credits', path: '/emissions/offsets', icon: 'offset-credits', permission: 'compliance.read' },
      { id: 'company-emissions-reports', title: 'Emission Reports', path: '/emissions/reports', icon: 'emission-reports', permission: 'compliance.read' }
    ]
  },
  // Company Admin - Communication (consolidated: notifications + messages)
  {
    id: 'company-communication',
    title: 'COMMUNICATION',
    domain: 'company',
    requiredPermissions: ['notifications.read', 'companies.read'],
    items: [
      { id: 'company-notifications', title: 'Notifications', path: '/company/notifications', icon: 'notifications', permission: 'notifications.read' },
      { id: 'company-messages', title: 'Messages', path: '/company/messages', icon: 'messages', permission: 'companies.read' }
    ]
  },
  // Company Admin - Settings
  {
    id: 'company-settings',
    title: 'SETTINGS',
    domain: 'company',
    requiredPermissions: ['users.read'],
    items: [
      { id: 'company-account', title: 'Account Settings', path: '/company/settings', icon: 'settings', permission: 'users.read' },
      { id: 'company-profile', title: 'Company Profile', path: '/company/profile', icon: 'company-dashboard', permission: 'companies.read' },
      { id: 'company-billing', title: 'Billing & Plans', path: '/company/billing', icon: 'billing', permission: 'companies.read' }
    ]
  },

  // =====================================================
  // COMPLIANCE USER SECTIONS (Compliance User only)
  // =====================================================
  {
    id: 'compliance-overview',
    title: 'COMPLIANCE OVERVIEW',
    domain: 'compliance',
    requiredPermissions: ['compliance.dashboard.view'],
    items: [
      {
        id: 'compliance-dashboard',
        title: 'Compliance Dashboard',
        path: '/compliance/dashboard',
        icon: 'compliance-dashboard',
        permission: 'compliance.dashboard.view'
      }
    ]
  },
  {
    id: 'my-work',
    title: 'MY WORK',
    domain: 'compliance',
    requiredPermissions: ['compliance.read'],
    items: [
      {
        id: 'assigned-pages',
        title: 'Assigned Pages',
        path: '/compliance/tasks',
        icon: 'assigned-pages',
        permission: 'compliance.read'
      },
      {
        id: 'pending-evidence',
        title: 'Pending Evidence',
        path: '/compliance/pending',
        icon: 'pending-evidence',
        permission: 'compliance.read'
      }
    ]
  },
  {
    id: 'compliance-work',
    title: 'COMPLIANCE WORK',
    domain: 'compliance',
    requiredPermissions: ['frameworks.read'],
    items: [
      {
        id: 'active-frameworks',
        title: 'Active Frameworks',
        path: '/compliance/frameworks',
        icon: 'active-frameworks',
        permission: 'frameworks.read'
      }
    ]
  },
  {
    id: 'compliance-documents',
    title: 'DOCUMENTS',
    domain: 'compliance',
    requiredPermissions: ['compliance.read'],
    items: [
      {
        id: 'my-uploads',
        title: 'My Uploads',
        path: '/compliance/uploads',
        icon: 'my-uploads',
        permission: 'compliance.read'
      },
      {
        id: 'reference-materials',
        title: 'Reference Materials',
        path: '/compliance/reference',
        icon: 'reference-materials',
        permission: 'compliance.read'
      }
    ]
  },
  {
    id: 'compliance-support',
    title: 'SUPPORT',
    domain: 'compliance',
    requiredPermissions: ['compliance.read'],
    items: [
      {
        id: 'messages',
        title: 'Messages',
        path: '/compliance/messages',
        icon: 'messages',
        permission: 'compliance.read'
      },
    ]
  },
  {
    id: 'compliance-management',
    title: 'COMPLIANCE MANAGEMENT',
    domain: 'compliance',
    requiredPermissions: ['compliance.read'],
    items: [
      { id: 'compliance-obligation-register', title: 'Obligation Register', path: '/compliance/obligations', icon: 'obligation-register', permission: 'compliance.read' },
      { id: 'compliance-task-management', title: 'Task Management', path: '/compliance/task-management', icon: 'assigned-pages', permission: 'compliance.read' },
      { id: 'compliance-capa', title: 'Corrective Actions (CAPA)', path: '/compliance/capa', icon: 'assigned-pages', permission: 'compliance.read' },
      { id: 'evidence-expiry', title: 'Evidence Expiry', path: '/compliance/evidence-expiry', icon: 'pending-evidence', permission: 'compliance.read' },
      { id: 'gap-assessments', title: 'Gap Assessments', path: '/compliance/gap-assessments', icon: 'compliance-dashboard', permission: 'compliance.read' },
      { id: 'compliance-control-tests', title: 'Control Testing', path: '/compliance/control-tests', icon: 'control-tests', permission: 'compliance.read' },
      { id: 'my-training', title: 'My Training', path: '/compliance/training', icon: 'assigned-pages', permission: 'compliance.read' },
      { id: 'compliance-cyber-essentials', title: 'Cyber Essentials', path: '/compliance/cyber-essentials', icon: 'compliance-structure', permission: 'compliance.read' }
    ]
  },
  {
    id: 'compliance-data-protection',
    title: 'GDPR & DATA PROTECTION',
    domain: 'compliance',
    requiredPermissions: ['compliance.read'],
    items: [
      { id: 'compliance-ropa', title: 'ROPA Register', path: '/compliance/ropa', icon: 'reference-materials', permission: 'compliance.read' }
    ]
  },
  {
    id: 'compliance-governance',
    title: 'GOVERNANCE & RISK',
    domain: 'compliance',
    requiredPermissions: ['company.compliance.manage'],
    items: [
      {
        id: 'policy-library',
        title: 'Policy Library',
        path: '/compliance/policies',
        icon: 'reference-materials',
        permission: 'company.compliance.manage'
      },
      {
        id: 'risk-register',
        title: 'Risk Register',
        path: '/compliance/risk-register',
        icon: 'compliance-structure',
        permission: 'company.compliance.manage'
      },
      {
        id: 'controls-library',
        title: 'Controls Library',
        path: '/compliance/controls',
        icon: 'compliance-structure',
        permission: 'company.compliance.manage'
      },
      {
        id: 'vendor-risk',
        title: 'Vendor Risk Register',
        path: '/compliance/vendor-risk',
        icon: 'compliance-structure',
        permission: 'company.compliance.manage'
      },
      { id: 'compliance-vendor-contracts', title: 'Vendor Contracts & DPA', path: '/compliance/vendor-contracts', icon: 'vendor-contracts', permission: 'company.compliance.manage' },
      {
        id: 'asset-inventory',
        title: 'Asset Inventory',
        path: '/compliance/assets',
        icon: 'compliance-structure',
        permission: 'company.compliance.manage'
      },
      { id: 'compliance-policy-attestations', title: 'Policy Attestations', path: '/compliance/policy-attestations', icon: 'policy-attestations', permission: 'company.compliance.manage' }
    ]
  },
  // Compliance User - Incident & Data Protection
  {
    id: 'compliance-incident-protection',
    title: 'INCIDENT & DATA PROTECTION',
    domain: 'compliance',
    requiredPermissions: ['compliance.read'],
    items: [
      { id: 'compliance-incidents', title: 'Incident Register', path: '/compliance/incidents', icon: 'incident-register', permission: 'compliance.read' },
      { id: 'compliance-privacy-reviews', title: 'Privacy Reviews', path: '/compliance/privacy-reviews', icon: 'privacy-reviews', permission: 'compliance.read' },
      { id: 'compliance-data-retention', title: 'Data Retention', path: '/compliance/data-retention', icon: 'data-retention', permission: 'compliance.read' }
    ]
  },
  // Compliance User - UK Regulatory
  {
    id: 'compliance-uk-regulatory',
    title: 'UK REGULATORY',
    domain: 'compliance',
    requiredPermissions: ['compliance.read'],
    items: [
      { id: 'compliance-onboarding', title: 'Compliance Scoping', path: '/compliance/onboarding', icon: 'onboarding', permission: 'compliance.read' },
      { id: 'compliance-statutory-filings', title: 'Statutory Filings', path: '/compliance/statutory-filings', icon: 'statutory-filings', permission: 'compliance.read' },
      { id: 'compliance-modern-slavery', title: 'Modern Slavery', path: '/compliance/modern-slavery', icon: 'modern-slavery', permission: 'compliance.read' }
    ]
  },
  {
    id: 'compliance-reporting',
    title: 'REPORTING',
    domain: 'compliance',
    requiredPermissions: ['compliance.read'],
    items: [
      {
        id: 'compliance-monitoring',
        title: 'Monitoring Dashboard',
        path: '/compliance/monitoring',
        icon: 'compliance-dashboard',
        permission: 'compliance.read'
      },
      {
        id: 'management-reports',
        title: 'Management Reports',
        path: '/compliance/management-reports',
        icon: 'reference-materials',
        permission: 'reports.create'
      },
      {
        id: 'compliance-timeline',
        title: 'Compliance Timeline',
        path: '/compliance/timeline',
        icon: 'compliance-dashboard',
        permission: 'compliance.read'
      }
    ]
  },

  // =====================================================
  // AUDITOR SECTIONS (Auditor only)
  // =====================================================
  // ── Auditor overview ──
  {
    id: 'audit-overview',
    title: 'AUDIT OVERVIEW',
    domain: 'audit',
    requiredPermissions: ['audit.dashboard.view'],
    items: [
      {
        id: 'auditor-dashboard',
        title: 'Dashboard',
        path: '/audit/dashboard',
        icon: 'auditor-dashboard',
        permission: 'audit.dashboard.view'
      }
    ]
  },
  {
    id: 'assessments',
    title: 'REVIEW WORK',
    domain: 'audit',
    requiredPermissions: ['compliance.read'],
    items: [
      {
        id: 'framework-reviews',
        title: 'Framework Reviews',
        path: '/audit/frameworks',
        icon: 'framework-manager',
        permission: 'frameworks.read',
        requiresCompanyScope: true,
      },
      {
        id: 'evidence-review',
        title: 'Evidence Review',
        path: '/audit/evidence',
        icon: 'evidence-library',
        permission: 'compliance.read',
        requiresCompanyScope: true,
      },
      {
        // Phase 3: page-level internal auditor review (transitions submitted → approved_for_audit)
        id: 'pages-pending-review',
        title: 'Pending Page Review',
        path: '/audit/pages-review',
        icon: 'evidence-library',
        permission: 'compliance.review',
        requiresCompanyScope: true,
      },
      {
        id: 'notes-observations',
        title: 'Notes & Observations',
        path: '/audit/notes',
        icon: 'notes-observations',
        permission: 'compliance.read',
        requiresCompanyScope: true,
      },
      {
        id: 'non-conformities',
        title: 'Non-Conformities',
        path: '/audit/non-conformities',
        icon: 'non-conformities',
        permission: 'compliance.read',
        requiresCompanyScope: true,
      },
      {
        id: 'audit-certifications',
        title: 'Certifications',
        path: '/audit/certifications',
        icon: 'compliance-structure',
        permission: 'certifications.manage'
      }
    ]
  },
  {
    id: 'reporting',
    title: 'REPORTING',
    domain: 'audit',
    requiredPermissions: ['compliance.read'],
    items: [
      {
        id: 'assessment-summary',
        title: 'Assessment Summary',
        path: '/audit/summary',
        icon: 'assessment-summary',
        permission: 'compliance.read',
        requiresCompanyScope: true,
      },
      {
        id: 'export-reports',
        title: 'Export Reports',
        path: '/audit/export',
        icon: 'export-reports',
        permission: 'reports.export',
        requiresCompanyScope: true,
      }
    ]
  },
  // ── Admin escape hatch: manual certificate issuance ──
  // External auditors do NOT see this — they go through /audit/dashboard so
  // each control gets a per-page decision tied to the certificate snapshot.
  // Reserved for System Admins for exceptional cases (paper audits, regulator
  // backfill, mid-migration data). Each issuance from here is logged as an
  // admin override in the audit trail.
  {
    id: 'certification-admin-override',
    title: 'CERTIFICATION (ADMIN)',
    domain: 'admin',
    requiredPermissions: ['certifications.manage'],
    items: [
      {
        id: 'issue-certification',
        title: 'Manual Issue Certificate',
        path: '/certification/issue',
        icon: 'issue-certification',
        permission: 'certifications.manage',
      },
    ],
  },
  {
    id: 'audit-support',
    title: 'SUPPORT',
    domain: 'audit',
    requiredPermissions: ['compliance.read'],
    items: [
      {
        id: 'audit-messages',
        title: 'Messages',
        path: '/audit/messages',
        icon: 'messages',
        permission: 'compliance.read'
      }
    ]
  },
  // Auditor - Compliance Review (read-only)
  {
    id: 'audit-compliance-review',
    title: 'COMPLIANCE REVIEW',
    domain: 'audit',
    requiredPermissions: ['compliance.read'],
    items: [
      { id: 'audit-monitoring', title: 'Monitoring Dashboard', path: '/compliance/monitoring', icon: 'compliance-dashboard', permission: 'compliance.read', requiresCompanyScope: true },
      { id: 'audit-management-reports', title: 'Management Reports', path: '/compliance/management-reports', icon: 'reference-materials', permission: 'reports.read', requiresCompanyScope: true }
    ]
  },
  // Auditor - Carbon Overview (read-only)
  {
    id: 'audit-carbon',
    title: 'CARBON & EMISSIONS',
    domain: 'audit',
    requiredPermissions: ['compliance.read'],
    items: [
      { id: 'audit-emissions-scopes', title: 'Scopes Dashboard', path: '/emissions/scopes', icon: 'emissions-dashboard', permission: 'compliance.read', requiresCompanyScope: true },
      { id: 'audit-emissions-reports', title: 'Emission Reports', path: '/emissions/reports', icon: 'emission-reports', permission: 'reports.read', requiresCompanyScope: true }
    ]
  },

  // =====================================================
  // CARBON MANAGEMENT SECTIONS
  // =====================================================
  {
    id: 'carbon-management',
    title: 'CARBON MANAGEMENT',
    domain: 'compliance',
    requiredPermissions: ['compliance.read'],
    items: [
      {
        id: 'emissions-scopes',
        title: 'Scopes Dashboard',
        path: '/emissions/scopes',
        icon: 'emissions-dashboard',
        permission: 'compliance.read'
      },
      {
        id: 'emissions-bulk-calculator',
        title: 'Bulk Calculator',
        path: '/emissions/bulk-calculator',
        icon: 'bulk-calculator',
        permission: 'compliance.read'
      },
      {
        id: 'emissions-targets',
        title: 'Reduction Targets',
        path: '/emissions/targets',
        icon: 'reduction-targets',
        permission: 'compliance.read'
      },
      {
        id: 'emissions-offsets',
        title: 'Offset Credits',
        path: '/emissions/offsets',
        icon: 'offset-credits',
        permission: 'compliance.read'
      },
      {
        id: 'emissions-reports',
        title: 'Emission Reports',
        path: '/emissions/reports',
        icon: 'emission-reports',
        permission: 'compliance.read'
      },
      {
        id: 'emissions-factors',
        title: 'Factor Library',
        path: '/emissions/factors',
        icon: 'factor-library',
        permission: 'compliance.read'
      }
    ]
  }
];

/**
 * Get navigation sections filtered by domain AND permissions
 * Navigation visibility requires BOTH domain match AND permission match
 *
 * @param hasPermission - Function to check if user has a specific permission
 * @param userDomain - User's domain (admin, company, compliance, audit)
 * @returns Filtered navigation sections with only items user can access
 */
export function getNavSectionsWithItems(
  hasPermission: (permission: string) => boolean,
  userDomain: 'admin' | 'partner' | 'company' | 'compliance' | 'audit' | 'emissions',
  userEmail?: string,
  hasCompanyScope: boolean = true,
): NavSection[] {
  const filtered = NAVIGATION_SECTIONS
    .filter(section => section.domain === userDomain)
    .map(section => {
      const items = section.items.filter(item => {
        if (!hasPermission(item.permission)) return false;
        if (item.allowedEmails && item.allowedEmails.length > 0) {
          const email = userEmail?.toLowerCase();
          if (!email || !item.allowedEmails.map(e => e.toLowerCase()).includes(email)) return false;
        }
        if (item.requiresCompanyScope && !hasCompanyScope) return false;
        return true;
      });
      return { ...section, items };
    })
    .filter(section => section.items.length > 0);

  // Universal tail section — a "What's new" changelog entry appended for
  // every domain so users of any role can review the platform release
  // history. Mirrors the /changelog route which is gated on authentication
  // only, not on permissions.
  filtered.push({
    id: 'platform-updates',
    title: 'PLATFORM UPDATES',
    domain: userDomain,
    requiredPermissions: [],
    items: [
      {
        id: 'changelog',
        title: "What's new",
        path: '/changelog',
        icon: 'changelog',
        permission: '__universal__',
      },
    ],
  } as NavSection);

  return filtered;
}

/**
 * Get all navigation items for user (flattened)
 *
 * @param hasPermission - Function to check if user has a specific permission
 * @param userPersona - User's primary role/persona
 * @returns Flattened array of navigation items user can access
 */
export function getAllNavItems(
  hasPermission: (permission: string) => boolean,
  userDomain: 'admin' | 'partner' | 'company' | 'compliance' | 'audit' | 'emissions',
  userEmail?: string
): NavItem[] {
  const sections = getNavSectionsWithItems(hasPermission, userDomain, userEmail);
  return sections.flatMap(section => section.items);
}

/**
 * Get navigation item by ID
 *
 * @param hasPermission - Function to check if user has a specific permission
 * @param userPersona - User's primary role/persona
 * @param itemId - The ID of navigation item to find
 * @returns The navigation item if found and user has permission, undefined otherwise
 */
export function getNavItemById(
  hasPermission: (permission: string) => boolean,
  userDomain: 'admin' | 'partner' | 'company' | 'compliance' | 'audit' | 'emissions',
  itemId: string
): NavItem | undefined {
  const sections = getNavSectionsWithItems(hasPermission, userDomain);
  for (const section of sections) {
    const item = section.items.find((item: NavItem) => item.id === itemId);
    if (item) {
      return item;
    }
  }
  return undefined;
}

/**
 * Get navigation section by ID
 *
 * @param hasPermission - Function to check if user has a specific permission
 * @param userPersona - User's primary role/persona
 * @param sectionId - The ID of navigation section to find
 * @returns The navigation section if found and has accessible items, undefined otherwise
 */
export function getNavSectionById(
  hasPermission: (permission: string) => boolean,
  userDomain: 'admin' | 'company' | 'compliance' | 'audit' | 'emissions',
  sectionId: string
): NavSection | undefined {
  const sections = getNavSectionsWithItems(hasPermission, userDomain);
  return sections.find(section => section.id === sectionId);
}

/**
 * Get default landing page based on user permissions and persona
 * Returns first accessible navigation item's path
 *
 * @param hasPermission - Function to check if user has a specific permission
 * @param userPersona - User's primary role/persona
 * @returns The path to default landing page
 */
export function getDefaultLandingPage(
  hasPermission: (permission: string) => boolean,
  userDomain: 'admin' | 'partner' | 'company' | 'compliance' | 'audit' | 'emissions'
): string {
  // Domain-scoped dashboard permissions for proper persona isolation
  // Only check the dashboard permission that matches the user's domain
  switch (userDomain) {
    case 'admin':
      if (hasPermission('admin.dashboard.view')) return '/admin/dashboard';
      break;
    case 'partner':
      if (hasPermission('partner.dashboard.view')) return '/partner/dashboard';
      break;
    case 'company':
      if (hasPermission('company.dashboard.view')) return '/company/dashboard';
      break;
    case 'compliance':
      if (hasPermission('compliance.dashboard.view')) return '/compliance/dashboard';
      break;
    case 'audit':
      if (hasPermission('audit.dashboard.view')) return '/audit/dashboard';
      break;
  }

  // Fallback to first accessible item within user's domain
  const items = getAllNavItems(hasPermission, userDomain);
  if (items.length > 0) {
    return items[0]?.path || '/login';
  }
  return '/login';
}
